/**
 * Mailurile din proba gratuită (2026-10-27): proprietarul nu poate face demo cu
 * fiecare firmă înscrisă singură, deci aplicația o duce pe om, pas cu pas, la
 * prima comparație și apoi la pachet. Patru mailuri, după zilele de la înscriere;
 * textul fiecăruia se potrivește cu ce a făcut firma până atunci.
 */

export type TrialMailStep = 'd1' | 'd3' | 'd10' | 'd13';

/** Ziua de la înscriere la care pleacă fiecare. */
const STEP_DAY: Record<TrialMailStep, number> = {
  d1: 1,
  d3: 3,
  d10: 10,
  d13: 13,
};

/** Un pas întârziat cu mai mult de atât nu mai pleacă (firmele vechi nu primesc nimic). */
const WINDOW_DAYS = 2;

const DAY = 86_400_000;

export interface TrialFacts {
  /** câți furnizori are în agendă */
  suppliers: number;
  /** câte cereri a trimis */
  sent: number;
  /** câte oferte a primit */
  offers: number;
  /** câte comenzi a făcut */
  orders: number;
  /** cererea trimisă la care duce butonul (cea mai nouă, cu cele mai multe oferte) */
  rfqId?: string | null;
}

/**
 * Ce mail i se cuvine firmei acum, sau null. Cel mult unul pe rulare: dacă serverul
 * a stat oprit, pleacă doar ultimul pas încă în fereastră, nu toate deodată.
 * Doar în probă (fără pachet) și doar pentru firmele înscrise de când există mailurile.
 */
export function trialMailDue(
  c: {
    createdAt: Date;
    trialEndsAt: Date | null;
    plan: string | null;
    sent: string[];
  },
  now: Date,
): TrialMailStep | null {
  if (c.plan || !c.trialEndsAt || c.trialEndsAt.getTime() <= now.getTime()) return null;
  const age = (now.getTime() - c.createdAt.getTime()) / DAY;
  const due = (Object.keys(STEP_DAY) as TrialMailStep[]).filter(
    (s) => age >= STEP_DAY[s] && age < STEP_DAY[s] + WINDOW_DAYS && !c.sent.includes(s),
  );
  return due.at(-1) ?? null;
}

/** Ora din România: mailurile pleacă doar între 9 și 18, nu noaptea. */
export function workingHour(now: Date, timeZone = 'Europe/Bucharest'): boolean {
  const h = Number(
    new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      hourCycle: 'h23',
      timeZone,
    }).format(now),
  );
  return h >= 9 && h < 18;
}

/** Următorul lucru util, după ce a făcut firma: pentru mailul din prima zi. */
export function nextTrialAction(f: TrialFacts): 'suppliers' | 'send' | 'offers' | 'compare' {
  if (!f.suppliers) return 'suppliers';
  if (!f.sent) return 'send';
  if (!f.offers) return 'offers';
  return 'compare';
}

/* ---------- unde duc butoanele (Ticket 08, P10) ---------- */

/** Ecranul din aplicație despre care vorbește un mail: butonul duce exact acolo. */
export type TrialScreen = 'site' | 'suppliers' | 'new' | 'rfqs' | 'plan' | { rfq: string };

/** `?pas=<cuvânt>`: cuvintele din link, în română, ca restul linkurilor aplicației. */
const SCREEN_WORD = {
  site: 'santier',
  suppliers: 'furnizori',
  new: 'cerere',
  rfqs: 'cereri',
  plan: 'pachet',
} as const;

/** Linkul butonului: o cerere anume (`?cerere=`) sau un ecran (`?pas=`). */
export function trialLink(base: string, to: TrialScreen): string {
  const root = base.replace(/\/+$/, '');
  return typeof to === 'string'
    ? `${root}/?pas=${SCREEN_WORD[to]}`
    : `${root}/?cerere=${encodeURIComponent(to.rfq)}`;
}

/** Invers, în aplicație: ce ecran cere `?pas=`, sau null. */
export function trialScreenOf(word: string | null): Exclude<TrialScreen, { rfq: string }> | null {
  const hit = (Object.keys(SCREEN_WORD) as (keyof typeof SCREEN_WORD)[]).find((k) => SCREEN_WORD[k] === word);
  return hit ?? null;
}
